import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, LifeBuoy } from "lucide-react";
import * as React from "react";

import {
  FEEDBACK_ATTACHMENT_ACCEPT,
  FEEDBACK_ATTACHMENT_MAX_BYTES,
  FEEDBACK_CATEGORIES,
  submitFeedback,
  type FeedbackCategory,
  type FeedbackResponse,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/ui/form-error";

const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 250;

export interface HelpFeedbackDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * HF-04: the Help & Feedback slide-out. Opened from the sidebar "Help and
 * Feedback" item for every role; overlays the active page (Sheet) so the user
 * keeps their context. Fire-and-forget to JSM (ADR-0027): submit -> POST
 * /feedback -> show the returned tracking key. No persistence, no history view.
 *
 * An optional single attachment (≤5MB, JPG/PNG/PDF) rides the multipart submit
 * and is forwarded to the JSM issue (CHP-813). The dynamic JSM request-type
 * fetch is still deferred (ADR-0027); the category list is the hardcoded enum
 * from the client, kept in lockstep with the backend `FeedbackCategory`.
 */
export function HelpFeedbackDrawer({ open, onOpenChange }: HelpFeedbackDrawerProps) {
  const { accessToken, data } = useCurrentUser();

  const [category, setCategory] = React.useState<FeedbackCategory | "">("");
  const [description, setDescription] = React.useState("");
  // Response Preference (support.md): "I would like a follow-up" is the default
  // — the safer choice for a support form.
  const [wantsFollowUp, setWantsFollowUp] = React.useState(true);
  // CHP-813: optional single attachment + a client-side validation message.
  // The backend re-validates by magic bytes; this is a UX pre-check only.
  const [attachment, setAttachment] = React.useState<File | null>(null);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<FeedbackResponse | null>(null);

  // CHP-773 / CHP-787 #2: the 48h note is role-aware. Partners get the
  // deals-flavoured wording; managers/admins get the sales-flavoured wording.
  const roleNote = React.useMemo(() => {
    if (data?.role === "channel_partner") {
      return "For deals related enquiries, we aim to get back to you within 48 hours.";
    }
    return "For sales related enquiries, we aim to get back to you within 48 hours on business days.";
  }, [data?.role]);

  const reset = React.useCallback(() => {
    setCategory("");
    setDescription("");
    setWantsFollowUp(true);
    setAttachment(null);
    setAttachmentError(null);
    setResult(null);
  }, []);

  const onAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setAttachment(null);
      setAttachmentError(null);
      return;
    }
    if (!(FEEDBACK_ATTACHMENT_ACCEPT as readonly string[]).includes(file.type)) {
      setAttachment(null);
      setAttachmentError("Attach a JPG, PNG, or PDF file.");
      e.target.value = "";
      return;
    }
    if (file.size > FEEDBACK_ATTACHMENT_MAX_BYTES) {
      setAttachment(null);
      setAttachmentError("Attachment must be 5MB or smaller.");
      e.target.value = "";
      return;
    }
    setAttachmentError(null);
    setAttachment(file);
  };

  const mutation = useMutation({
    mutationFn: () =>
      submitFeedback(
        accessToken!,
        {
          category: category as FeedbackCategory,
          description: description.trim(),
          wants_follow_up: wantsFollowUp,
          // Auto-captured — the triager sees which screen the user was on.
          // Truncate to the backend's max_length (2000) so a long deep link
          // doesn't 422 an otherwise-valid submission.
          current_url: typeof window !== "undefined" ? window.location.href.slice(0, 2000) : null,
        },
        attachment,
      ),
    onSuccess: (data) => setResult(data),
  });

  // Closing the drawer resets to a clean form so the next open starts fresh
  // (and a stale success/error never flashes on re-open).
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      mutation.reset();
      reset();
    }
    onOpenChange(next);
  };

  const trimmedDescription = description.trim();
  const descriptionValid =
    trimmedDescription.length >= DESCRIPTION_MIN && trimmedDescription.length <= DESCRIPTION_MAX;
  const formInvalid = !category || !descriptionValid || !!attachmentError;
  const submitDisabled = mutation.isPending || formInvalid;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" aria-hidden />
            Help and Feedback
          </SheetTitle>
          <SheetDescription>
            Found a bug, hit a snag, or have an idea? Send it straight to our team.
          </SheetDescription>
        </SheetHeader>

        {result ? (
          <SuccessState trackingKey={result.tracking_key} />
        ) : (
          <form
            className="mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto"
            onSubmit={(e) => {
              e.preventDefault();
              if (submitDisabled) return;
              mutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="feedback-category">Request type</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as FeedbackCategory)}
                disabled={mutation.isPending}
              >
                <SelectTrigger
                  id="feedback-category"
                  aria-label="Request type"
                  data-testid="feedback-request-type"
                >
                  <SelectValue placeholder="Choose a request type…" />
                </SelectTrigger>
                <SelectContent>
                  {FEEDBACK_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-summary">Summary</Label>
              <Textarea
                id="feedback-summary"
                data-testid="feedback-summary"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX}
                minLength={DESCRIPTION_MIN}
                rows={3}
                required
                disabled={mutation.isPending}
                placeholder="A quick description of your issue…"
              />
              <p className="text-xs text-muted-foreground">
                {trimmedDescription.length}/{DESCRIPTION_MAX} — minimum {DESCRIPTION_MIN}{" "}
                characters.
              </p>
            </div>

            <fieldset className="space-y-1.5" disabled={mutation.isPending}>
              <legend className="text-sm font-medium leading-none">Contact preference</legend>
              <div role="radiogroup" aria-label="Contact preference" className="space-y-2">
                <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
                  <input
                    type="radio"
                    name="feedback-response-preference"
                    value="follow-up"
                    checked={wantsFollowUp}
                    onChange={() => setWantsFollowUp(true)}
                    className="mt-0.5"
                    data-testid="feedback-response-follow-up"
                  />
                  <span className="font-medium">I would like a follow-up</span>
                </label>
                <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
                  <input
                    type="radio"
                    name="feedback-response-preference"
                    value="no-feedback"
                    checked={!wantsFollowUp}
                    onChange={() => setWantsFollowUp(false)}
                    className="mt-0.5"
                    data-testid="feedback-response-none"
                  />
                  <span className="font-medium">No response required</span>
                </label>
              </div>
            </fieldset>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-attachment">Attachment (optional)</Label>
              <input
                id="feedback-attachment"
                data-testid="feedback-attachment"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={onAttachmentChange}
                disabled={mutation.isPending}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/80"
              />
              {attachmentError ? (
                <FormError>{attachmentError}</FormError>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {attachment ? attachment.name : "Max 5MB — JPG, PNG, or PDF."}
                </p>
              )}
            </div>

            {mutation.isError ? (
              <FormError>
                Something went wrong sending your request. Please try again, or reach out to our
                support team directly.
              </FormError>
            ) : null}

            <div className="mt-auto space-y-3 border-t border-border pt-4">
              <Button
                type="submit"
                data-testid="feedback-submit"
                className="w-full gap-2"
                disabled={formInvalid}
                loading={mutation.isPending}
                loadingLabel="Submitting your request…"
              >
                Submit request
              </Button>
              <p className="text-xs text-muted-foreground">{roleNote}</p>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SuccessState({ trackingKey }: { trackingKey: string }) {
  return (
    <div className="mt-6 flex flex-1 flex-col items-center justify-center gap-4 text-center">
      <CheckCircle2 className="h-12 w-12 text-success" aria-hidden />
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-foreground">Message sent.</h3>
        <p className="text-sm text-muted-foreground">
          Our team will review your request. For sales related enquiries, we aim to respond within
          48 business hours.
        </p>
      </div>
      <div className="space-y-1" data-testid="feedback-reference">
        <span className="text-xs text-muted-foreground">Reference ID</span>
        <p className="font-mono text-sm font-semibold text-muted-foreground">{trackingKey}</p>
      </div>
    </div>
  );
}
